import React from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '../../../utils/cn';

/**
 * Button Component
 * 
 * A flexible button component with multiple variants, sizes, and states.
 * Supports icons, loading states, and full customization.
 * 
 * @param {string} variant - Button style: 'primary' | 'secondary' | 'ghost' | 'destructive'
 * @param {string} size - Button size: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
 * @param {boolean} loading - Show loading spinner
 * @param {boolean} disabled - Disable button
 * @param {ReactNode} leftIcon - Icon to display on left
 * @param {ReactNode} rightIcon - Icon to display on right
 * @param {boolean} fullWidth - Make button full width
 * @param {string} className - Additional CSS classes
 * @param {ReactNode} children - Button content
 */
export const Button = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  leftIcon,
  rightIcon,
  fullWidth = false,
  className,
  children,
  type = 'button',
  onClick,
  ...props
}) => {
  // Base styles - Applied to all buttons
  const baseStyles = cn(
    'inline-flex items-center justify-center',
    'font-medium',
    'transition-all duration-200',
    'focus:outline-none focus:ring-2 focus:ring-offset-2',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    'select-none',
  );

  // Variant styles
  const variantStyles = {
    primary: cn(
      'bg-primary-600 text-white',
      'hover:bg-primary-700 active:bg-primary-800',
      'focus:ring-primary-500',
      'shadow-sm hover:shadow-md',
    ),
    secondary: cn(
      'bg-slate-100 text-slate-700',
      'hover:bg-slate-200 active:bg-slate-300',
      'focus:ring-slate-500',
      'border border-slate-200',
    ),
    ghost: cn(
      'bg-transparent text-slate-700',
      'hover:bg-slate-100 active:bg-slate-200',
      'focus:ring-slate-500',
    ),
    destructive: cn(
      'bg-red-600 text-white',
      'hover:bg-red-700 active:bg-red-800',
      'focus:ring-red-500',
      'shadow-sm hover:shadow-md',
    ),
  };

  // Size styles
  const sizeStyles = {
    xs: cn(
      'h-7 px-2 text-xs',
      'rounded-md',
      'gap-1.5',
    ),
    sm: cn(
      'h-8 px-3 text-sm',
      'rounded-md',
      'gap-2',
    ),
    md: cn(
      'h-10 px-4 text-sm',
      'rounded-md',
      'gap-2',
    ),
    lg: cn(
      'h-11 px-6 text-base',
      'rounded-md',
      'gap-2',
    ),
    xl: cn(
      'h-12 px-8 text-base',
      'rounded-lg',
      'gap-2',
    ),
  };

  // Icon sizes based on button size
  const iconSizes = {
    xs: 14,
    sm: 16,
    md: 20,
    lg: 20,
    xl: 24,
  };

  // Width styles
  const widthStyles = fullWidth ? 'w-full' : '';

  // Render icon with proper size
  const renderIcon = (icon, position) => {
    if (!icon) return null;
    
    return React.cloneElement(icon, {
      size: icon.props.size || iconSizes[size],
      className: cn(
        icon.props.className,
        loading && position === 'left' && 'opacity-0',
      ),
    });
  };

  return (
    <button
      type={type}
      disabled={disabled || loading}
      onClick={onClick}
      className={cn(
        baseStyles,
        variantStyles[variant],
        sizeStyles[size],
        widthStyles,
        className,
      )}
      {...props}
    >
      {/* Loading spinner */}
      {loading && (
        <Loader2 
          size={iconSizes[size]} 
          className="animate-spin absolute"
        />
      )}

      {/* Left icon */}
      {leftIcon && renderIcon(leftIcon, 'left')}

      {/* Button text */}
      {children && (
        <span className={cn(loading && 'opacity-0')}>
          {children}
        </span>
      )}

      {/* Right icon */}
      {rightIcon && renderIcon(rightIcon, 'right')}
    </button>
  );
};

/**
 * IconButton Component
 * 
 * Button with icon only, no text
 * Automatically applies square padding and centers icon
 */
export const IconButton = ({
  icon,
  size = 'md',
  variant = 'ghost',
  className,
  ...props
}) => {
  const sizeStyles = {
    xs: 'h-7 w-7 p-0',
    sm: 'h-8 w-8 p-0',
    md: 'h-10 w-10 p-0',
    lg: 'h-11 w-11 p-0',
    xl: 'h-12 w-12 p-0',
  };

  return (
    <Button
      size={size}
      variant={variant}
      className={cn(sizeStyles[size], className)}
      {...props}
    >
      {icon}
    </Button>
  );
};

/**
 * ButtonGroup Component
 * 
 * Groups multiple buttons together with connected borders
 */
export const ButtonGroup = ({
  children,
  className,
  ...props
}) => {
  return (
    <div 
      className={cn(
        'inline-flex',
        '[&>button]:rounded-none',
        '[&>button:first-child]:rounded-l-md',
        '[&>button:last-child]:rounded-r-md',
        '[&>button:not(:last-child)]:border-r-0',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
};

export default Button;